import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

// Component to handle dynamic sitemap serving at /sitemap.xml route
export const DynamicSitemap = () => {
  const [sitemap, setSitemap] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Check if we're on the sitemap route
    if (window.location.pathname === '/sitemap.xml') {
      fetchCachedSitemap();
    }
  }, []);

  const fetchCachedSitemap = async () => {
    try {
      setIsLoading(true);
      
      // Try to get sitemap from edge function (most up-to-date)
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      
      const response = await fetch(`${supabaseUrl}/functions/v1/auto-update-sitemap`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const sitemapXML = await response.text();
        setSitemap(sitemapXML);
        
        // Set proper content type
        document.querySelector('meta[name="content-type"]')?.setAttribute('content', 'application/xml');
      } else {
        // Fallback to cached sitemap from admin_settings
        const { data, error } = await supabase
          .from('admin_settings')
          .select('setting_value')
          .eq('setting_key', 'latest_sitemap')
          .single();
        
        if (data?.setting_value && typeof data.setting_value === 'object' && 'xml' in data.setting_value) {
          setSitemap(data.setting_value.xml as string);
        } else {
          // Minimal fallback
          setSitemap(`<?xml version="1.0" encoding="UTF-8"?>
<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">
  <url>
    <loc>${window.location.origin}</loc>
    <lastmod>${new Date().toISOString()}</lastmod>
    <changefreq>daily</changefreq>
    <priority>1.0</priority>
  </url>
  <url>
    <loc>${window.location.origin}/blog</loc>
    <lastmod>${new Date().toISOString()}</lastmod>
    <changefreq>daily</changefreq>
    <priority>0.9</priority>
  </url>
</urlset>`);
        }
      }
    } catch (error) {
      console.error('Failed to fetch sitemap:', error);
      setSitemap(`<?xml version="1.0" encoding="UTF-8"?>
<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">
  <url>
    <loc>${window.location.origin}</loc>
    <lastmod>${new Date().toISOString()}</lastmod>
    <changefreq>daily</changefreq>
    <priority>1.0</priority>
  </url>
</urlset>`);
    } finally {
      setIsLoading(false);
    }
  };

  // If we're on the sitemap route, return the XML content
  if (window.location.pathname === '/sitemap.xml') {
    if (isLoading) {
      return (
        <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>
          Loading sitemap...
        </pre>
      );
    }

    return (
      <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>
        {sitemap}
      </pre>
    );
  }

  return null;
};
